import type { FC } from 'react';
import { useEffect, useState } from 'react';
import cookies from 'js-cookie';
import { publish, subscribe, unsubscribe } from '@alfa-bank/site-event-bus';
import { getParamsFromURL } from '@alfa-bank/site-helpers-common/url/get-params-from-url';

import { regionsDictionary } from './regions-dictionary';
import type { TMortgageDataManager, TPublishRegion } from './models';
import { transformRegions, getRegionById, detectRegionFromService } from './regions';

const publishCurrentRegion = (region?: TPublishRegion) =>
    publish('current_region_response', region);

const publishRegionsList = (regionsList: TPublishRegion[]) =>
    publish('regions_response', regionsList);

export const MortgageDataManager: FC<TMortgageDataManager> = ({
    defaultRegion = 'msc',
    regions = regionsDictionary,
    geoCookieName = 'site_city',
    geoUrlParamName = 'filterCity',
}) => {
    const transformedRegions = transformRegions(regions);
    const defaultRegionObj = transformedRegions.find(
        ({ id }: { id: string }) => id === defaultRegion
    );
    const [currentRegion, setCurrentRegion] = useState(defaultRegionObj);

    // Эффект на запрос current_region_request
    useEffect(() => {
        const eventFunction = () => {
            publishCurrentRegion(currentRegion);
        };

        subscribe('current_region_request', eventFunction);

        return function cleanup() {
            unsubscribe('current_region_request', eventFunction);
        };
    });

    // Эффект на запрос current_region_changed
    useEffect(() => {
        const eventFunction = (payload: { event: string; value: unknown } & TPublishRegion) => {
            const { event, value, ...data } = payload;

            setCurrentRegion(data);
            publishCurrentRegion(data);
        };

        subscribe('current_region_changed', eventFunction);

        return function cleanup() {
            unsubscribe('current_region_changed', eventFunction);
        };
    });

    // Эффект на запрос regions_request
    useEffect(() => {
        /* istanbul ignore next */
        const eventFunction = () => publishRegionsList(transformedRegions);

        subscribe('regions_request', eventFunction);

        return function cleanup() {
            unsubscribe('regions_request', eventFunction);
        };
    });

    useEffect(() => {
        // Отправим событие со списком регионов
        publishRegionsList(transformedRegions);

        // Отправим событие c дефолтным городом
        publishCurrentRegion(defaultRegionObj);

        const queryValues = getParamsFromURL({
            url: window.location.href,
            requiredParams: [geoUrlParamName],
        });

        const regionId = queryValues[geoUrlParamName];

        // Если регион получен из get-параметров
        if (regionId) {
            const region = getRegionById({
                regionId,
                regions: transformedRegions,
            });

            publishCurrentRegion(region);

            return;
        }

        // Иначе пытаемся достать регион из куки
        const regionPath = cookies.get(geoCookieName);

        if (regionPath) {
            (async function detectRegion() {
                const region = await detectRegionFromService(transformedRegions, regionPath);

                publishCurrentRegion(region);
            })();
        }
    }, []);

    return null;
};
